import React, { useState, useMemo } from 'react';
import { Clock, Users } from 'lucide-react';
import { Card, Button } from '../components/ui';
import { ModalMinutaje } from '../components/modals/ModalMinutaje';
import { useJornadas } from '../hooks/useJornadas';
import { useJugadoresEquipoActivo } from '../hooks/useJugadoresEquipoActivo';
import { JornadaDto } from '../types';

/**
 * Página de gestión de jornadas
 * Muestra todas las jornadas del equipo activo del entrenador
 */
const JornadasComponent: React.FC = () => {
  
  // Usar solo useJugadoresEquipoActivo aquí
  const { jugadores: jugadoresEquipo, equipo, loading: loadingJugadores, error: errorJugadores } = useJugadoresEquipoActivo();
  
  // Pasar los datos al hook useJornadas (solo las variables necesarias)
  const {
    jornadas,
    loading: loadingJornadas,
    error: errorJornadas,
    cargarJornadas
  } = useJornadas(jugadoresEquipo, equipo);

  // Memoizar estados combinados para evitar re-renders
  const estadoCombinado = useMemo(() => ({
    loading: loadingJugadores || loadingJornadas,
    error: errorJugadores || errorJornadas,
    jornadasCount: jornadas.length,
    jugadoresCount: jugadoresEquipo.length,
    equipoId: equipo?.id
  }), [loadingJugadores, loadingJornadas, errorJugadores, errorJornadas, jornadas.length, jugadoresEquipo.length, equipo?.id]);

  
  const [modalAbierto, setModalAbierto] = useState(false);
  const [jornadaSeleccionada, setJornadaSeleccionada] = useState<JornadaDto | null>(null);

  const handleAbrirMinutaje = (jornada: JornadaDto) => {
    setJornadaSeleccionada(jornada);
    setModalAbierto(true);
  };

  /**
   * Maneja el cierre del modal de minutaje sin recargar datos
   */
  const handleCerrarModal = () => {
    setModalAbierto(false);
    setJornadaSeleccionada(null);
    // No recargar jornadas al cancelar
  };

  /**
   * Maneja el cierre del modal después de guardar datos
   */
  const handleDatosActualizados = () => {
    // Recargar jornadas solo cuando se guardan datos
    cargarJornadas();
  };

  if (estadoCombinado.loading) {
    return (
      <div className="flex-1 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-secondary-400">Cargando jornadas...</div>
        </div>
      </div>
    );
  }

  if (estadoCombinado.error) {
    return (
      <div className="flex-1 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-400">{estadoCombinado.error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-white">{equipo?.nombre || 'Jornadas'}</h1>
        </div>

        {/* Información */}
        <Card variant="glass">
          <div className="p-4">
            <p className="text-slate-300 text-sm">
              Selecciona una jornada para registrar los minutos de juego de tus jugadores.
            </p>
          </div>
        </Card>

        {/* Lista de jornadas */}
        <div className="space-y-4">
          {jornadas.length === 0 ? (
            <Card variant="glass">
              <div className="p-6 text-center">
                <Users className="w-12 h-12 text-slate-500 mx-auto mb-3 drop-shadow-md" />
                <p className="text-slate-300 font-medium">No hay jornadas disponibles</p>
                <p className="text-slate-500 text-sm mt-1">
                  Contacta con el administrador para crear jornadas
                </p>
              </div>
            </Card>
          ) : (
            jornadas.map((jornada) => (
              <Card 
                key={jornada.id} 
                variant="glass"
                className="hover:border-emerald-500/50 hover:bg-slate-900/40 transition-all duration-300 hover:shadow-[0_0_15px_rgba(16,185,129,0.15)]"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div>
                        <h3 className="text-white font-medium">
                          Jornada {jornada.numero}
                        </h3>
                        {jornada.equipoRival && (
                          <p className="text-slate-400 text-sm">
                            vs {jornada.equipoRival}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleAbrirMinutaje(jornada)}
                      className="flex items-center space-x-2"
                    >
                      <Clock className="w-4 h-4" />
                      <span>Minutaje</span>
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Modal de minutaje */}
      {modalAbierto && jornadaSeleccionada && (
          <ModalMinutaje
              jornada={jornadaSeleccionada}
              jugadores={jugadoresEquipo}
              onClose={handleCerrarModal}
              onDataUpdated={handleDatosActualizados}
          />
      )}
    </div>
  );
};

// Exportar componente memoizado
export const Jornadas = React.memo(JornadasComponent);
export default Jornadas;